import { useState, useEffect } from 'react';
import axios from 'axios';
import { Bug, Lightbulb, Trash2, Clock, CheckCircle2, XCircle, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE as API } from '../../config';

const STATUS_OPTS = [
  { value: 'new', label: 'New', color: 'bg-blue-500', text: 'text-blue-400' },
  { value: 'in_review', label: 'In Review', color: 'bg-amber-500', text: 'text-amber-400' },
  { value: 'resolved', label: 'Resolved', color: 'bg-emerald-500', text: 'text-emerald-400' },
  { value: 'dismissed', label: 'Dismissed', color: 'bg-slate-500', text: 'text-slate-400' },
];

export default function DevFeedback() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');

  const fetchFeedback = async () => {
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterType !== 'all') params.fb_type = filterType;
      const res = await axios.get(`${API}/dev/feedback`, { params, withCredentials: true });
      setItems(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchFeedback(); }, [filterStatus, filterType]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/dev/feedback/${id}`, { status }, { withCredentials: true });
      setItems((prev) => prev.map((i) => i.feedback_id === id ? { ...i, status } : i));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error('Failed to update');
    }
  };

  const deleteFeedback = async (id) => {
    try {
      await axios.delete(`${API}/dev/feedback/${id}`, { withCredentials: true });
      setItems((prev) => prev.filter((i) => i.feedback_id !== id));
      toast.success('Deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const counts = {
    total: items.length,
    bugs: items.filter((i) => i.type === 'bug').length,
    suggestions: items.filter((i) => i.type === 'suggestion').length,
    new: items.filter((i) => i.status === 'new').length,
  };

  if (loading) return <div className="p-6 text-slate-400">Loading...</div>;

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="dev-feedback-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Feedback Inbox</h1>
          <p className="text-sm text-slate-400 mt-1">
            {counts.total} total &middot; {counts.bugs} bugs &middot; {counts.suggestions} suggestions
            {counts.new > 0 && <span className="ml-2 text-blue-400">({counts.new} new)</span>}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-5">
        <div className="flex items-center gap-1 bg-slate-900 rounded-lg p-1">
          {['all', 'bug', 'suggestion'].map((t) => (
            <button
              key={t}
              onClick={() => setFilterType(t)}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${filterType === t ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-slate-300'}`}
              data-testid={`filter-type-${t}`}
            >
              {t === 'all' ? 'All Types' : t === 'bug' ? 'Bugs' : 'Suggestions'}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 bg-slate-900 rounded-lg p-1">
          {['all', ...STATUS_OPTS.map((s) => s.value)].map((s) => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${filterStatus === s ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-slate-300'}`}
              data-testid={`filter-status-${s}`}
            >
              {s === 'all' ? 'All' : STATUS_OPTS.find((o) => o.value === s)?.label}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {items.length === 0 ? (
        <div className="text-center py-16 text-slate-500">
          <MessageSquare className="w-10 h-10 mx-auto mb-3 text-slate-600" />
          <p className="text-sm">No feedback yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((fb) => {
            const statusInfo = STATUS_OPTS.find((s) => s.value === fb.status) || STATUS_OPTS[0];
            return (
              <div
                key={fb.feedback_id}
                className="bg-slate-900 border border-slate-800 rounded-lg px-5 py-4 hover:border-slate-700 transition-colors"
                data-testid={`feedback-item-${fb.feedback_id}`}
              >
                <div className="flex items-start gap-3">
                  {/* Type icon */}
                  <div className={`mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${fb.type === 'bug' ? 'bg-red-500/10' : 'bg-amber-500/10'}`}>
                    {fb.type === 'bug' ? <Bug className="w-4 h-4 text-red-400" /> : <Lightbulb className="w-4 h-4 text-amber-400" />}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <p className="text-sm font-medium text-white">{fb.title}</p>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${statusInfo.text} bg-slate-800`}>
                        {statusInfo.label}
                      </span>
                    </div>
                    {fb.description && <p className="text-xs text-slate-400 mb-2 line-clamp-2">{fb.description}</p>}
                    <div className="flex items-center gap-3 text-[11px] text-slate-500">
                      <span>{fb.submitted_by}</span>
                      <span>{fb.page_url}</span>
                      <span>{new Date(fb.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {fb.status !== 'in_review' && (
                      <button onClick={() => updateStatus(fb.feedback_id, 'in_review')} title="In Review" className="p-1.5 rounded hover:bg-slate-800 text-slate-500 hover:text-amber-400 transition-colors" data-testid={`fb-review-${fb.feedback_id}`}>
                        <Clock className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {fb.status !== 'resolved' && (
                      <button onClick={() => updateStatus(fb.feedback_id, 'resolved')} title="Resolve" className="p-1.5 rounded hover:bg-slate-800 text-slate-500 hover:text-emerald-400 transition-colors" data-testid={`fb-resolve-${fb.feedback_id}`}>
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {fb.status !== 'dismissed' && (
                      <button onClick={() => updateStatus(fb.feedback_id, 'dismissed')} title="Dismiss" className="p-1.5 rounded hover:bg-slate-800 text-slate-500 hover:text-slate-300 transition-colors" data-testid={`fb-dismiss-${fb.feedback_id}`}>
                        <XCircle className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button onClick={() => deleteFeedback(fb.feedback_id)} title="Delete" className="p-1.5 rounded hover:bg-slate-800 text-slate-500 hover:text-red-400 transition-colors" data-testid={`fb-delete-${fb.feedback_id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
